import React, { useContext, useState, useEffect, useRef } from "react";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, rad, icBox, listRow, inpS } from "../../constants/styles";
import Fld from "../common/Fld";
import Avatar from "../common/Avatar";
import { uploadPhoto } from "../../utils/supabaseClient";
import { getPlanUsage } from "../../utils/plans";
import UpgradeModal from "../common/UpgradeModal";
import ChangePasswordModal from "../common/ChangePasswordModal";
import { api } from "../../api";
import { can } from "../../utils/permissions";
import { parseLegacyBankDetails } from "../../utils/bankDetails";

function SettingsPage() {
  const { t, h, doLogout, go, flash, plan, props, refreshData, role, orgRole, settings, setSettings, openUpgradeOnSettings, setOpenUpgradeOnSettings, userName, setUserAvatar } = useContext(Ctx);
  const [showUpgrade, setShowUpgrade] = useState(false);
  // Landlord profile-picture (DP) uploader state. The DP lives inside
  // the landlord_info JSON blob (key: avatar_url) and is persisted with
  // the rest of the landlord info on Save.
  const llAvatarInputRef = useRef(null);
  const [llAvatarUploading, setLlAvatarUploading] = useState(false);
  // If a deep-link asked us to pop the Upgrade modal on arrival
  // (e.g. the dashboard's expired-plan banner), do it once and clear
  // the flag so navigating away and back doesn't keep re-popping it.
  useEffect(() => {
    if (openUpgradeOnSettings) {
      setShowUpgrade(true);
      setOpenUpgradeOnSettings(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openUpgradeOnSettings]);
  const [billingTxns, setBillingTxns] = useState([]);
  const [showAllTxns, setShowAllTxns] = useState(false);
  // Cashfree — landlord connects their OWN merchant account so tenants
  // can pay rent online and it settles directly to the landlord's bank.
  const [cfStatus, setCfStatus] = useState({ status: "not_setup" });
  const [cfForm, setCfForm] = useState({ client_id: "", client_secret: "", webhook_secret: "", env: "sandbox" });
  const [cfBusy, setCfBusy] = useState(false);
  const [cfError, setCfError] = useState("");
  const [cfHelp, setCfHelp] = useState(false);
  const setCF = (k, v) => setCfForm(p => ({ ...p, [k]: v }));
  // Payment & Security settings (moved from PaySettingsPage)
  const [upiName, setUpiName] = useState(settings.upi_name || "");
  const [upiId, setUpiId] = useState(settings.upi_id || "");
  // Structured bank fields (replaces the legacy free-form `bank_details`
  // textarea — tenants now see each field with its own copy button).
  // If the new fields are empty but legacy `bank_details` text exists,
  // parseLegacyBankDetails() lazy-parses the most common patterns so
  // existing landlords don't have to re-type everything after the upgrade.
  const _legacy = parseLegacyBankDetails(settings.bank_details);
  const [bankAcctName, setBankAcctName] = useState(settings.bank_account_name || _legacy.account_name || "");
  const [bankAcctNum,  setBankAcctNum]  = useState(settings.bank_account_number || _legacy.account_number || "");
  const [bankIfsc,     setBankIfsc]     = useState(settings.bank_ifsc || _legacy.ifsc || "");
  const [bankName,     setBankName]     = useState(settings.bank_name || _legacy.bank_name || "");
  const [paySettingsSaved, setPaySettingsSaved] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const savePaySettings = () => {
    // Compose a human-readable bank_details blob too — keeps backward-
    // compat with any tenant client still on a build that reads the old
    // single-text field.
    const composed = [
      bankAcctName ? `Account Name: ${bankAcctName}` : "",
      bankAcctNum  ? `A/C No: ${bankAcctNum}` : "",
      bankIfsc     ? `IFSC: ${bankIfsc.toUpperCase()}` : "",
      bankName     ? `Bank: ${bankName}` : "",
    ].filter(Boolean).join(", ");
    const s = {
      upi_name: upiName,
      upi_id: upiId,
      bank_account_name: bankAcctName.trim(),
      bank_account_number: bankAcctNum.replace(/\s+/g, ""),
      bank_ifsc: bankIfsc.trim().toUpperCase(),
      bank_name: bankName.trim(),
      bank_details: composed,
    };
    setSettings(prev => ({ ...prev, ...s }));
    Object.keys(s).forEach(k => api.saveSetting(k, s[k]));
    setPaySettingsSaved(true);
    flash(t.settingsSaved || "Settings saved!");
    setTimeout(() => setPaySettingsSaved(false), 2000);
  };

  // Landlord Info — used to auto-populate rental-agreement PDFs.
  // Stored as a JSON blob under settings.landlord_info (same shape the
  // inline editor in AgreementPage.js uses, so both UIs stay in sync).
  const parseLLInfo = () => { try { return JSON.parse(settings?.landlord_info || '{}'); } catch { return {}; } };
  const [llInfo, setLLInfo] = useState(parseLLInfo());
  const [llSaved, setLLSaved] = useState(false);
  const setLI = (k, v) => setLLInfo(p => ({ ...p, [k]: v }));

  // Landlord profile picture (DP) lives inside the landlord_info blob
  // (key: avatar_url). Upload compresses to a small avatar; the URL is
  // held in llInfo and persisted with the rest of the landlord info on
  // Save. The TopBar DP updates immediately on save.
  const onPickLlAvatar = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setLlAvatarUploading(true);
    try {
      const url = await uploadPhoto(file, "avatar_landlord", { maxWidth: 400, quality: 0.85 });
      setLI('avatar_url', url);
      flash(t.dpUpdated || "Picture updated — tap Save to apply");
    } catch (err) {
      flash(err?.message || "Upload failed", "error");
    } finally {
      setLlAvatarUploading(false);
    }
  };

  const saveLLInfo = async () => {
    if (!(llInfo.name || '').trim()) {
      flash("Full name is required", "error");
      return;
    }
    const em = (llInfo.email || '').trim();
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      flash("Enter a valid email address", "error");
      return;
    }
    const blob = JSON.stringify(llInfo);
    await api.saveSetting("landlord_info", blob);
    // Mirror the email to org_users.email — the canonical column the password-reset
    // OTP reads (the landlord_info settings blob isn't visible to that lookup).
    api.updateEmail(em).catch(() => {});
    setSettings(s => ({ ...s, landlord_info: blob }));
    // Reflect the new DP in the TopBar immediately (own avatar).
    if (typeof setUserAvatar === "function") setUserAvatar(llInfo.avatar_url || "");
    setLLSaved(true);
    flash("Landlord info saved");
    setTimeout(() => setLLSaved(false), 2000);
  };
  const usage = getPlanUsage(plan.type, props.length, plan.maxProperties);
  const isPro = plan.type !== "free";
  const isMaxPlan = plan.type === "pro"; // Only Pro is the highest tier — all others can upgrade
  const isPerProperty = plan.pricingModel === "per_property";
  const isSubsOff = plan.subscriptionsDisabled === true || plan.type === "unlimited";
  // Trial state: free org but live pricing model is per-property.
  // Server flags this with plan.isTrial = true. Visual branding is
  // Per Property (so it matches the active billing model) but the
  // free 2-property cap and feature limits still apply.
  const isPerPropertyTrial = plan.isTrial === true && isPerProperty;
  // Friendly display name. Falls through:
  //   1. plan.name from the server (e.g. "Per Property", "Pro")
  //   2. title-cased plan.type when name is missing
  //   3. legacy "Pro Plan" / "Free Plan" labels as last resort
  const friendlyPlanName = plan.name
    || (plan.type ? plan.type.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ') : null)
    || (isPro ? "Pro Plan" : "Free Plan");
  // Subtitle picks the right copy for the plan context — and avoids
  // echoing the title word-for-word when we don't have something
  // more specific to say.
  const planSubtitle = isSubsOff
    ? "All features included · No expiry"
    : isPerPropertyTrial
      ? `Free trial · ${plan.maxProperties || 2} properties max`
      : isPerProperty
        ? "Pay per property · Unlimited"
        : isPro
          ? (t.planProActive || "Active subscription")
          : (t.planFreeDesc || "Basic plan");

  // Fetch billing history for landlords
  useEffect(() => {
    if (role === "landlord") {
      api.getBillingHistory().then(d => {
        if (d && d.success && d.transactions) setBillingTxns(d.transactions);
      }).catch(e => console.error("Billing history fetch error:", e));
    }
  }, [role]);

  // Fetch the landlord's Cashfree connection status (for the online-rent card)
  useEffect(() => {
    if (role === "landlord") {
      api.getCashfreeStatus().then(d => { if (d && d.success) setCfStatus(d); }).catch(() => {});
    }
  }, [role]);

  const connectCF = async () => {
    if (!cfForm.client_id.trim() || !cfForm.client_secret.trim()) { setCfError("App ID and Secret Key are required"); return; }
    setCfBusy(true); setCfError("");
    try {
      const d = await api.connectCashfree(cfForm);
      if (d && d.success) {
        setCfStatus({ status: "verified", env: d.env, key_last4: d.key_last4 });
        setCfForm({ client_id: "", client_secret: "", webhook_secret: "", env: cfForm.env });
        flash(t.cashfreeConnected || "Cashfree connected — you can now collect rent online");
      } else {
        setCfError(d.detail || d.error || "Could not connect. Check your keys and environment.");
      }
    } catch (e) { setCfError("Connection failed. Please try again."); }
    finally { setCfBusy(false); }
  };
  const disconnectCF = async () => {
    setCfBusy(true);
    try { const d = await api.disconnectCashfree(); if (d && d.success) setCfStatus({ status: "not_setup" }); }
    catch (_) { /* ignore */ }
    finally { setCfBusy(false); }
  };

  // Billing helpers
  const verifiedTxn = billingTxns.find(tx => tx.status === "verified");
  const upgradedDate = verifiedTxn ? new Date(verifiedTxn.verified_at || verifiedTxn.created_at).toLocaleDateString() : null;
  const statusChip = (st) => {
    const colors = { verified: h.gn, created: h.wn || "#f59e0b", failed: h.rd };
    const labels = { verified: t.billingVerified || "Verified", created: t.billingPending || "Pending", failed: t.billingFailed || "Failed" };
    return { bg: (colors[st] || h.sc) + "15", color: colors[st] || h.sc, label: labels[st] || st };
  };

  // Reports & Export, Dark Mode and Rent Act Guide were removed from this
  // page \u2014 they now live in the top-right gear menu (single source of
  // truth), so duplicating them here was redundant.
  // Per-role rows. Team Management is a landlord org-owner feature.
  // "View Agreement" routes to tenAgr (the TENANT's lease page), so it's
  // only meaningful for tenants \u2014 it was wrongly showing to service
  // persons (no lease) and landlords (who use the Agreements page).
  const items = [
    ...(role === "landlord" && can(orgRole, "team") ? [["Team Management", "\u2192", () => go("team"), "team"]] : []),
    ...(role === "tenant" ? [[t.viewAgr, "\u2192", () => go("tenAgr"), "doc"]] : []),
    [t.logout, "\u2192", () => doLogout(), "logout"]
  ];

  // Collapsible Settings cards \u2014 each card starts MINIMIZED; tap the
  // title row to expand, tap again to collapse. `card()` is a plain
  // render helper (NOT a nested component) so the form inputs inside the
  // Payment / Landlord cards don't remount and lose focus on every key.
  const [openCard, setOpenCard] = useState({});
  const card = (id, opts, body) => {
    const { emoji, iconColor = h.ac, title, right } = opts || {};
    const isOpen = !!openCard[id];
    return (
      <div style={{ ...crd(h), marginBottom: sp.xs, padding: 0, overflow: "hidden" }}>
        <button type="button" onClick={() => setOpenCard(p => ({ ...p, [id]: !p[id] }))} aria-expanded={isOpen}
          style={{ width: "100%", display: "flex", alignItems: "center", gap: sp.sm, padding: sp.md, background: "transparent", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
          {emoji != null && <div style={icBox(iconColor, 32)}><span style={{ fontSize: 14 }}>{emoji}</span></div>}
          <span style={{ color: h.tx, fontWeight: 600, fontSize: 13, flex: 1, minWidth: 0 }}>{title}</span>
          {right}
          <span style={{ color: h.tt || h.sc, fontSize: 13, flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .25s" }}>{"\u25be"}</span>
        </button>
        {isOpen && <div style={{ padding: "0 " + sp.md + "px " + sp.md + "px" }}>{body}</div>}
      </div>
    );
  };

  return <div style={{ paddingTop: sp.lg }}>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, marginBottom: sp.lg, letterSpacing: "-0.2px" }}>{t.settings}</h2>

    {/* Plan card — landlord ONLY. Billing / subscription / property
        usage is an org-owner concern; tenants and service persons have
        no plan and were wrongly seeing the landlord org's plan data. */}
    {role === "landlord" && card("plan", { emoji: isMaxPlan ? "\u2B50" : isPro ? "\uD83D\uDE80" : "\uD83D\uDCE6", iconColor: isMaxPlan ? h.gn : h.ac, title: friendlyPlanName, right: isPro && <span style={bdg(plan.planStatus === 'expired' ? `${h.rd}15` : plan.daysRemaining != null && plan.daysRemaining <= 7 ? `${h.wn}15` : `${h.gn}15`, plan.planStatus === 'expired' ? h.rd : plan.daysRemaining != null && plan.daysRemaining <= 7 ? h.wn : h.gn)}>{plan.planStatus === 'expired' ? "Expired" : "\u2713 " + (t.planActive || "Active")}</span> }, <>
      {/* Plan validity — show for paid plans */}
      {isPro && plan.planEnd && <div style={{ background: plan.planStatus === 'expired' ? `${h.rd}08` : plan.daysRemaining != null && plan.daysRemaining <= 7 ? `${h.wn}08` : `${h.ac}05`, borderRadius: rad.sm, padding: `${sp.sm}px ${sp.sm + 4}px`, marginBottom: sp.sm + 2, border: `1px solid ${plan.planStatus === 'expired' ? h.rd + '20' : plan.daysRemaining != null && plan.daysRemaining <= 7 ? h.wn + '20' : h.bd}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: h.ts, fontSize: 11 }}>{t.planValidity || "Plan Validity"}</span>
          <span style={{ color: plan.planStatus === 'expired' ? h.rd : plan.daysRemaining != null && plan.daysRemaining <= 7 ? h.wn : h.tx, fontWeight: 700, fontSize: 12 }}>
            {plan.daysRemaining != null ? (plan.daysRemaining > 0 ? `${plan.daysRemaining} ${t.daysLeft || "days left"}` : (t.planExpired || "Expired")) : "—"}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          {plan.planStart && <span style={{ color: h.ts, fontSize: 10 }}>{t.from || "From"}: {new Date(plan.planStart).toLocaleDateString()}</span>}
          {plan.planEnd && <span style={{ color: h.ts, fontSize: 10 }}>{t.to || "To"}: {new Date(plan.planEnd).toLocaleDateString()}</span>}
        </div>
        {plan.planStatus === 'expired' && (() => {
          // Mirror the server's grace-period math: cron auto-downgrades
          // at day 7 post-expiry. Showing the count here so the user
          // knows exactly how long they have before features auto-revert.
          if (plan.type === 'free' || !plan.planEnd) {
            return <div style={{ color: h.rd, fontSize: 10, fontWeight: 600, marginTop: 4 }}>{"⚠️ " + (t.renewToRestore || "Renew now to restore all features")}</div>;
          }
          const daysSince = Math.floor((Date.now() - new Date(plan.planEnd).getTime()) / (1000 * 60 * 60 * 24));
          const graceDays = typeof plan?.gracePeriodDays === 'number' ? plan.gracePeriodDays : 7;
          const graceLeft = Math.max(0, graceDays - daysSince);
          return <div style={{ color: h.rd, fontSize: 10, fontWeight: 600, marginTop: 4, lineHeight: 1.5 }}>
            {graceLeft > 0
              ? `⏳ ${graceLeft} day${graceLeft === 1 ? '' : 's'} left in grace period — renew before downgrade to Free`
              : `⚠️ Grace period ended — auto-downgraded to Free`}
          </div>;
        })()}
        {plan.daysRemaining != null && plan.daysRemaining > 0 && plan.daysRemaining <= 7 && <div style={{ color: h.wn, fontSize: 10, fontWeight: 600, marginTop: 4 }}>{"⚠️ " + (t.planExpiringSoon || "Expiring soon — renew to continue")}</div>}
      </div>}
      {/* Property usage bar — show for all plans */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: sp.xs + 2 }}>
        <span style={{ color: h.ts, fontSize: 11 }}>{t.planPropsUsed || "Properties used"}</span>
        <span style={{ color: h.tx, fontWeight: 600, fontSize: 12 }}>{usage.used} / {usage.limit === 999999 ? "\u221E" : usage.limit}</span>
      </div>
      <div style={{ background: h.ib, borderRadius: 3, height: 6, overflow: "hidden" }}>
        <div style={{ background: usage.atLimit ? h.wn : h.ac, height: "100%", width: `${Math.min(usage.pct, 100)}%`, borderRadius: 3, transition: "width 0.4s ease" }} />
      </div>
      {usage.atLimit && !isMaxPlan && <div style={{ color: h.wn, fontSize: 10, marginTop: sp.xs + 2, fontWeight: 500 }}>{t.planAtLimit || "Property limit reached"}</div>}
      {isPro && upgradedDate && <div style={{ color: h.ts, fontSize: 10, marginTop: sp.xs + 2 }}>{t.upgradedOn || "Upgraded on"}: {upgradedDate}</div>}

      {/* Per-Property trial bill forecast — shown only when this org
          is on the Per-Property trial (free, but the live model is
          per-property). Tells the landlord exactly what they'd owe
          if they subscribed today, scaled by their current count. */}
      {isPerPropertyTrial && plan.perProperty && (
        <div style={{
          marginTop: sp.sm + 2,
          padding: `${sp.sm}px ${sp.sm + 2}px`,
          background: `${h.ac}06`,
          border: `1px solid ${h.ac}25`,
          borderRadius: rad.sm,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
            <span style={{ color: h.ts, fontSize: 11, fontWeight: 500 }}>
              Bill if subscribed today
            </span>
            <span style={{ color: h.ac, fontWeight: 700, fontSize: 13 }}>
              ₹{Math.round((plan.perProperty.projectedBillPaise || plan.perProperty.currentBillPaise || 0) / 100).toLocaleString('en-IN')}
              <span style={{ color: h.sc, fontWeight: 500, fontSize: 10 }}> /mo</span>
            </span>
          </div>
          <div style={{ color: h.tt || h.sc, fontSize: 10, lineHeight: 1.4 }}>
            {plan.perProperty.propertyCount} {plan.perProperty.propertyCount === 1 ? "property" : "properties"} × ₹{Math.round((plan.perProperty.unitPriceMonthly || 0) / 100)}
            {plan.perProperty.bulkThreshold > 0 && (
              <> · Volume rate ₹{Math.round((plan.perProperty.bulkUnitPriceMonthly || 0) / 100)}/prop above {plan.perProperty.bulkThreshold}</>
            )}
          </div>
        </div>
      )}
      {/* Upgrade / Change Plan button — hidden when subscriptions
          are OFF platform-wide (every landlord already has full
          access, no upgrade path makes sense) or when the user is
          on the highest fixed-tier plan. */}
      {!isMaxPlan && !isSubsOff && <button onClick={() => setShowUpgrade(true)} style={{ marginTop: sp.sm + 2, width: "100%", padding: `${sp.sm + 2}px`, borderRadius: rad.sm, border: isPro ? `1.5px solid ${h.ac}` : "none", background: isPro ? "transparent" : (h.pp || h.ac), color: isPro ? h.ac : "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700, boxShadow: isPro ? "none" : `0 2px 8px ${h.pp || h.ac}30`, transition: "all 0.2s ease", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        {plan.planStatus === 'expired' ? (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
            <span>{t.renewPlan || "Renew Plan"}</span>
          </>
        ) : isPro ? (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
            <span>{t.changePlan || "Change Plan"}</span>
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
            <span>{t.planUpgrade || "Upgrade"}</span>
          </>
        )}
      </button>}
      {/* Promo-mode hint \u2014 replaces the upgrade CTA when subscriptions
          are OFF platform-wide. Frames free access positively without
          leaking admin terminology. */}
      {isSubsOff && (
        <div style={{
          marginTop: sp.sm + 2,
          padding: `${sp.sm + 2}px`,
          borderRadius: rad.sm,
          background: `${h.gn}08`,
          border: `1px solid ${h.gn}25`,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={h.gn} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span style={{ color: h.gn, fontSize: 11, fontWeight: 600, lineHeight: 1.4 }}>
            All features are currently free for every landlord. Enjoy!
          </span>
        </div>
      )}
      {isMaxPlan && <div style={{ marginTop: sp.sm + 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: h.gn, fontSize: 11, fontWeight: 600 }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z" />
        </svg>
        <span>{t.maxPlanMsg || "You're on our best plan!"}</span>
      </div>}
    </>)}

    {/* Billing history card — landlord only (billingTxns is only fetched
        for landlords, so this is also empty for other roles). */}
    {role === "landlord" && billingTxns.length > 0 && card("billing", { title: t.billingHistory || "Billing History", right: (billingTxns.length > 2 && !showAllTxns) ? <span onClick={(e) => { e.stopPropagation(); setShowAllTxns(true); }} style={{ color: h.ac, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{t.viewAll || "View All"} ({billingTxns.length})</span> : (showAllTxns ? <span onClick={(e) => { e.stopPropagation(); setShowAllTxns(false); }} style={{ color: h.ts, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{t.showLess || "Show Less"}</span> : null) }, <>
      {(showAllTxns ? billingTxns : billingTxns.slice(0, 2)).map((tx, i, arr) => {
        const chip = statusChip(tx.status);
        const date = new Date(tx.created_at).toLocaleDateString();
        const amt = tx.amount ? `\u20B9${Math.round(tx.amount / 100)}` : "";
        return <div key={tx.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: `${sp.sm}px 0`, borderBottom: i < arr.length - 1 ? `1px solid ${h.bd}` : "none" }}>
          <div>
            <div style={{ color: h.tx, fontSize: 12, fontWeight: 500 }}>{(tx.plan_type || "").charAt(0).toUpperCase() + (tx.plan_type || "").slice(1)} Plan {amt && <span style={{ color: h.ts }}>({amt})</span>}</div>
            <div style={{ color: h.tt, fontSize: 10 }}>{date}</div>
          </div>
          <span style={bdg(chip.bg, chip.color)}>{chip.label}</span>
        </div>;
      })}
    </>)}

    {showUpgrade && <UpgradeModal h={h} t={t} used={usage.used} limit={usage.limit} currentPlan={plan.type} onClose={() => setShowUpgrade(false)} onUpgradeSuccess={async () => { await refreshData(); const hist = await api.getBillingHistory(); setBillingTxns(hist.transactions || []); flash(t.billingSuccess || "Plan Upgraded!"); }} />}

    {showChangePwd && <ChangePasswordModal h={h} t={t} onClose={() => setShowChangePwd(false)} onSuccess={(msg) => flash(msg)} />}

    {/* Landlord Info — used to auto-populate agreement PDFs.
        Reached via the '⚙️ Edit in Settings' button on the Agreement page. */}
    {role === "landlord" && card("landlord", { emoji: "\uD83C\uDFE0", title: "Landlord Info (for rental agreements)" }, <>
        <div style={{ color: h.sc, fontSize: 11, marginBottom: sp.sm, lineHeight: 1.4 }}>
          Used as the first party in every rental agreement you generate.
          Fill this once, reuse across all your agreements.
        </div>

        {/* Profile picture (DP) — saved inside the landlord_info blob.
            Shown across the app (TopBar etc.) after Save. */}
        <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.md }}>
          <div style={{ position: "relative", width: 64, height: 64 }}>
            <Avatar src={llInfo.avatar_url} name={llInfo.name || userName} size={64} ring={`2px solid ${h.ac}30`} fontSize={24} />
            {llAvatarUploading && (
              <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 18, height: 18, border: "3px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              </div>
            )}
            <button
              type="button"
              onClick={() => llAvatarInputRef.current && llAvatarInputRef.current.click()}
              disabled={llAvatarUploading}
              aria-label="Change profile picture"
              style={{ position: "absolute", bottom: -2, right: -2, width: 26, height: 26, borderRadius: "50%", background: h.ac, color: "#fff", border: `2px solid ${h.cd}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: llAvatarUploading ? "default" : "pointer", boxShadow: "0 2px 6px rgba(0,0,0,0.25)", fontSize: 13, lineHeight: 1 }}
            >
              {"📷"}
            </button>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>Profile photo</div>
            <div style={{ color: h.sc, fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>
              {llAvatarUploading ? "Uploading…" : "Shown across the app. Tap the camera to change."}
            </div>
            {llInfo.avatar_url && !llAvatarUploading && (
              <button type="button" onClick={() => setLI('avatar_url', '')} style={{ background: "none", border: "none", color: h.rd, cursor: "pointer", fontSize: 11, fontWeight: 600, padding: 0, marginTop: 4 }}>
                Remove
              </button>
            )}
          </div>
          <input ref={llAvatarInputRef} type="file" accept="image/*" onChange={onPickLlAvatar} style={{ display: "none" }} />
        </div>

        <Fld l="Full Name" v={llInfo.name || ''} set={v => setLI('name', v)} h={h} ph="Your full name" />
        <Fld l="Email — for password recovery" v={llInfo.email || ''} set={v => setLI('email', v)} h={h} ph="you@example.com" />

        <div style={{ display: "flex", gap: sp.sm, marginBottom: sp.sm }}>
          <div style={{ flex: 2 }}>
            <Fld l="Parent / Guardian" v={llInfo.parent || ''} set={v => setLI('parent', v)} h={h} ph="e.g. Ramakrishnan" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ color: h.ts, fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.3px" }}>Relation</label>
            <select
              value={llInfo.relation || 'son of'}
              onChange={e => setLI('relation', e.target.value)}
              style={{ ...inpS(h), fontSize: 13 }}
            >
              <option value="son of">Son of</option>
              <option value="daughter of">Daughter of</option>
              <option value="wife of">Wife of</option>
              <option value="husband of">Husband of</option>
              <option value="child of">Child of</option>
            </select>
          </div>
        </div>

        <Fld l="Aadhaar (last 4 digits)" v={llInfo.aadhaar || ''} set={v => setLI('aadhaar', String(v || '').replace(/\D/g, '').slice(-4))} h={h} ph="1234" />
        <Fld l="Address" v={llInfo.address || ''} set={v => setLI('address', v)} h={h} ph="12, Odai Street" />

        <div style={{ display: "flex", gap: sp.sm, marginBottom: sp.sm }}>
          <div style={{ flex: 1 }}>
            <Fld l="District" v={llInfo.district || ''} set={v => setLI('district', v)} h={h} ph="District" />
          </div>
          <div style={{ flex: 1 }}>
            <Fld l="Taluk" v={llInfo.taluk || ''} set={v => setLI('taluk', v)} h={h} ph="Taluk" />
          </div>
        </div>
        <Fld l="State" v={llInfo.state || 'Tamil Nadu'} set={v => setLI('state', v)} h={h} ph="Tamil Nadu" />

        <button
          onClick={saveLLInfo}
          style={{ width: "100%", padding: `${sp.sm + 4}px`, borderRadius: rad.sm, border: "none", background: llSaved ? h.gn : h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, marginTop: sp.sm, transition: "background 0.2s" }}
        >
          {llSaved ? "\u2713 Saved!" : "Save Landlord Info"}
        </button>
    </>)}

    {/* Payment Info — UPI & Bank Details. Landlord-only: these are the
        org's rent-collection details. The server already blocks
        non-landlords from saving (403), but the editable form was still
        rendering for tenants / service persons — confusing, and it
        surfaced the landlord's payout config in an edit form to people
        who don't collect money. Tenants who legitimately need to SEE
        these (to pay rent) get them read-only on the Pay-Now screen. */}
    {role === "landlord" && card("payment", { emoji: "\uD83D\uDCB3", title: t.paymentInfo || "Payment Info" }, <>
      <Fld l={t.payeeName || "Payee Name"} v={upiName} set={setUpiName} h={h} ph="Your Name" />
      <Fld l={t.upiId || "UPI ID"} v={upiId} set={setUpiId} h={h} ph="username@upi" />

      {/* Structured bank-account fields. Tenants see each row with its
          own copy button on the Pay-Now screen, so they can paste
          values straight into their banking app one at a time without
          fishing them out of a single comma-separated blob. */}
      <div style={{ marginTop: sp.md, paddingTop: sp.sm, borderTop: `1px dashed ${h.bd}` }}>
        <div style={{ color: h.ts, fontSize: 11, fontWeight: 600, marginBottom: sp.sm, textTransform: "uppercase", letterSpacing: "0.3px" }}>
          {t.bankDetails || "Bank Details"} <span style={{ color: h.tt, fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>(for NEFT / IMPS / RTGS)</span>
        </div>
        <Fld l="Account Holder Name" v={bankAcctName} set={setBankAcctName} h={h} ph="As per bank passbook" />
        <Fld l="Account Number"      v={bankAcctNum}  set={setBankAcctNum}  h={h} ph="617201505523" />
        <div style={{ display: "flex", gap: sp.sm }}>
          <div style={{ flex: 1 }}>
            <Fld l="IFSC"      v={bankIfsc} set={setBankIfsc} h={h} ph="ICIC0006172" />
          </div>
          <div style={{ flex: 1 }}>
            <Fld l="Bank Name" v={bankName} set={setBankName} h={h} ph="ICICI Bank" />
          </div>
        </div>
      </div>

    {/* Save Payment */}
    <button onClick={savePaySettings} style={{ width: "100%", padding: `${sp.sm + 4}px`, borderRadius: rad.sm, border: "none", background: paySettingsSaved ? h.gn : h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, marginBottom: sp.md, transition: "background 0.2s" }}>
      {paySettingsSaved ? "\u2713 Saved!" : (t.savePayment || "Save Payment Info")}
    </button>
    </>)}

    {/* Security — opens proper Change Password flow */}
    {role === "landlord" && card("cashfreeRent", { emoji: "\uD83C\uDFE6", iconColor: h.gn, title: t.onlineRent || "Online rent (Cashfree)" }, <>
      <div style={{ color: h.sc, fontSize: 11, marginBottom: sp.md, lineHeight: 1.5 }}>
        Connect your own Cashfree account so tenants can pay rent online — money settles directly to your bank (we never hold it). Leave it off to keep using manual UPI / Cash / Bank.
      </div>
      {cfStatus.status === "verified" ? (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: rad.sm, background: `${h.gn}10`, border: `1px solid ${h.gn}30`, marginBottom: sp.sm }}>
            <span style={{ color: h.gn, fontWeight: 800 }}>✓</span>
            <span style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>Connected</span>
            <span style={{ color: h.sc, fontSize: 11 }}>{cfStatus.env === "production" ? "Live" : "Sandbox"} · key ••••{cfStatus.key_last4 || "----"}</span>
          </div>
          <button onClick={disconnectCF} disabled={cfBusy} style={{ width: "100%", padding: `${sp.sm + 2}px`, borderRadius: rad.sm, border: `1px solid ${h.rd}40`, background: "transparent", color: h.rd, cursor: cfBusy ? "wait" : "pointer", fontSize: 13, fontWeight: 600 }}>
            {cfBusy ? "\u2026" : "Disconnect"}
          </button>
        </div>
      ) : (
        <>
          {/* Self-serve "How to connect" guide so landlords can onboard
              without help. Toggled to keep the form uncluttered. */}
          <button onClick={() => setCfHelp(v => !v)} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600, padding: 0, marginBottom: cfHelp ? sp.sm : sp.md }}>
            <span>{"ℹ️"}</span> {cfHelp ? "Hide guide" : "How to connect (2 min)"}
            <span style={{ fontSize: 10, transform: cfHelp ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>{"▾"}</span>
          </button>
          {cfHelp && (
            <div style={{ background: `${h.ac}08`, border: `1px solid ${h.ac}20`, borderRadius: rad.sm, padding: sp.md, marginBottom: sp.md, fontSize: 12, lineHeight: 1.6, color: h.tx }}>
              <ol style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 8 }}>
                <li>Create or open your <a href="https://merchant.cashfree.com/" target="_blank" rel="noopener noreferrer" style={{ color: h.ac, fontWeight: 700 }}>Cashfree dashboard</a> (free sign-up).</li>
                <li>Choose the environment with the top toggle: <b>Test</b> to try it safely, or <b>Production</b> for real money (needs KYC + your bank account).</li>
                <li>Open <b>Developers {"→"} API Keys</b>. Copy the <b>App ID</b> and <b>Secret Key</b> into the fields below.</li>
                <li>Set <b>Environment</b> below to match your keys, then tap <b>Test & Connect</b>.</li>
              </ol>
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px dashed ${h.bd}`, color: h.sc, fontSize: 11 }}>
                {"🔒"} Your keys are encrypted and never shared. Online rent lands <b>directly in your bank</b> {"—"} we never hold it.
              </div>
            </div>
          )}
          <Fld l="Cashfree App ID" v={cfForm.client_id} set={v => setCF("client_id", v)} h={h} ph="e.g. CF1234567890ABCD" />
          <Fld l="Cashfree Secret Key" v={cfForm.client_secret} set={v => setCF("client_secret", v)} h={h} type="password" ph="cfsk_…" />
          <label style={{ color: h.ts, fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.3px" }}>Environment</label>
          <select value={cfForm.env} onChange={e => setCF("env", e.target.value)} style={{ ...inpS(h), fontSize: 13, marginBottom: sp.sm }}>
            <option value="sandbox">Sandbox (testing)</option>
            <option value="production">Production (live money)</option>
          </select>
          {cfError && <div style={{ background: `${h.rd}10`, color: h.rd, padding: "9px 12px", borderRadius: rad.sm, fontSize: 12, fontWeight: 500, marginBottom: sp.sm, border: `1px solid ${h.rd}25` }}>{cfError}</div>}
          <button onClick={connectCF} disabled={cfBusy} style={{ width: "100%", padding: `${sp.sm + 4}px`, borderRadius: rad.sm, border: "none", background: cfBusy ? h.sc : h.gn, color: "#fff", cursor: cfBusy ? "wait" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {cfBusy ? "Verifying\u2026" : "Test & Connect"}
          </button>
        </>
      )}
    </>)}

    {card("security", { emoji: "\uD83D\uDD12", iconColor: h.rd, title: t.security || "Security" }, <>
      <button onClick={() => setShowChangePwd(true)} style={{ width: "100%", padding: `${sp.sm + 2}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: "transparent", color: h.tx, cursor: "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{t.changePassword || "Change Password"}</span>
        <span style={{ color: h.sc }}>{"\u2192"}</span>
      </button>
    </>)}

    {/* Language picker removed — language is now set from the gear menu
        dropdown (single source of truth). */}

    {/* Settings list */}
    <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
      {items.map(([l, v, a, icon], i) => <div key={i} onClick={a} style={{
        ...listRow(h, i === items.length - 1),
        gap: sp.sm
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.sm + 2 }}>
          <div style={icBox(l === t.logout ? h.rd : h.ac, 32)}>
            <span style={{ fontSize: 14 }}>{icon === "settings" ? "\u2699\uFE0F" : icon === "doc" ? "\uD83D\uDCC4" : icon === "help" ? "\u2753" : icon === "download" ? "\u2B07\uFE0F" : "\uD83D\uDEAA"}</span>
          </div>
          <span style={{ color: l === t.logout ? h.rd : h.tx, fontWeight: 500, fontSize: 14 }}>{l}</span>
        </div>
        <span style={{ color: l === t.logout ? h.rd : h.ac, fontWeight: 600, fontSize: 13 }}>{v}</span>
      </div>)}
    </div>
  </div>;
}

export default SettingsPage;
